import React, { useState, useEffect, useCallback } from 'react';
import { getInventoryList } from '../services/api';
import { DataTable, FormDatePicker, FormSelect } from '../shared';
import moment from 'moment';
import { Box, Link, MenuItem } from '@mui/material';
import InventoryDateDetailDrawer from './inventory/InventoryDateDetailDrawer';
import { detectThemeSync, watchThemeChanges } from '../utils/themeUtils';
import './InventoryList.css';
import { formatHotelDate } from '../utils/dateUtils';


const TODAY = moment().startOf('day');

const InventoryList = () => {
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const [inventoryData, setInventoryData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedDateIso, setSelectedDateIso] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [filters, setFilters] = useState({
    date: '',
    booking_percentage: '',
  });
  const [sortConfig, setSortConfig] = useState({ column: 'date', direction: 'desc' });
  const [pagination, setPagination] = useState({
    page: 0,
    perPage: 10,
    totalRecords: 0,
  });

  const fetchInventoryData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const sortColumn = sortConfig.column || 'date';
      const sortDirection = sortConfig.direction || 'desc';
      const sortParam = sortDirection === 'desc' ? `-${sortColumn}` : sortColumn;

      const params = {
        page: pagination.page + 1,
        per_page: pagination.perPage,
        sort: sortParam,
        ...(filters.date ? { date_from: filters.date, date_to: filters.date } : {}),
        ...(filters.booking_percentage ? { booking_percentage: filters.booking_percentage } : {}),
      };

      const response = await getInventoryList(params);

      if (response && response.Data) {
        setInventoryData(response.Data);
        setPagination((prev) => ({
          ...prev,
          totalRecords: response.total_records || 0,
        }));
      }
    } catch (err) {
      setError(err.message || 'Failed to fetch inventory data');
    } finally {
      setLoading(false);
    }
  }, [filters, sortConfig, pagination.page, pagination.perPage]);

  useEffect(() => {
    fetchInventoryData();
  }, [fetchInventoryData]);

  useEffect(() => {
    const cleanup = watchThemeChanges(setCurrentTheme);
    return cleanup;
  }, []);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handleSortChange = (newSortConfig) => {
    setSortConfig(newSortConfig);
    setPagination((prev) => ({ ...prev, page: 0 }));
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
  };

  const handlePerPageChange = (newPerPage) => {
    setPagination((prev) => ({ ...prev, perPage: newPerPage, page: 0 }));
  };

  const openDateDetail = (row) => {
    const iso = row.date_iso || (row.date ? moment(row.date).format('YYYY-MM-DD') : null);
    if (!iso) return;
    setSelectedDateIso(iso);
    setDrawerOpen(true);
  };

  const renderBookingPercentage = (value) => {
    const percentage = parseFloat(value) || 0;
    const roundedPercentage = Math.round(percentage * 100) / 100;
    const percentageInt = Math.min(100, Math.round(percentage));
    const percentageDisplay = `${roundedPercentage.toFixed(2)}%`;

    let colorClass = 'progress-wrap--yellow';
    if (percentageInt >= 100) {
      colorClass = 'progress-wrap--violet';
    } else if (percentageInt >= 70) {
      colorClass = 'progress-wrap--pink';
    } else if (percentageInt >= 40) {
      colorClass = 'progress-wrap--blue';
    }

    return (
      <div className={`progress-wrap progress-wrap--middle ${colorClass}`}>
        <p className="progress__label">{percentageDisplay}</p>
        <div className="progress">
          <div
            className="progress-bar"
            style={{ width: `${percentageInt}%` }}
            role="progressbar"
            aria-valuenow={roundedPercentage}
            aria-valuemin="0"
            aria-valuemax="100"
          >
            {percentageDisplay}
          </div>
        </div>
      </div>
    );
  };

  const columns = [
    {
      field: 'date',
      label: 'Date',
      sortable: true,
      render: (value, row) => {
        const label = value ? formatHotelDate(value) : '—';
        return (
          <Link
            component="button"
            type="button"
            underline="hover"
            onClick={() => openDateDetail(row)}
            sx={{
              fontWeight: 700,
              color: 'inherit',
              cursor: 'pointer',
              textAlign: 'left',
            }}
          >
            {label}
          </Link>
        );
      },
    },
    {
      field: 'total_inventory',
      label: 'Total Inventory',
      sortable: true,
      align: 'right',
      render: (value) => <strong>{value ? value.toLocaleString('en-IN') : 0}</strong>,
    },
    {
      field: 'booked_count',
      label: 'Booked',
      sortable: true,
      align: 'right',
      render: (value) => <strong>{value ? value.toLocaleString('en-IN') : 0}</strong>,
    },
    {
      field: 'not_booked_count',
      label: 'Not Booked',
      sortable: true,
      align: 'right',
      render: (value, row) => {
        const count = value ?? row.available_count ?? 0;
        return <strong>{count.toLocaleString('en-IN')}</strong>;
      },
    },
    {
      field: 'offline_booked_count',
      label: 'Offline Booked',
      sortable: true,
      align: 'right',
      render: (value, row) => (value ?? row.direct_booking_count ?? 0).toLocaleString('en-IN'),
    },
    {
      field: 'online_booked_count',
      label: 'Online Booked',
      sortable: true,
      align: 'right',
      render: (value, row) => (value ?? row.online_booking_count ?? 0).toLocaleString('en-IN'),
    },
    {
      field: 'booking_percentage',
      label: 'Booking %',
      sortable: true,
      align: 'center',
      render: (value) => renderBookingPercentage(value),
    },
  ];

  const filterRowCells = columns.map((column) => {
    switch (column.field) {
      case 'date':
        return (
          <FormDatePicker
            key={column.field}
            label="Date"
            value={filters.date ? moment(filters.date) : null}
            maxDate={TODAY}
            onChange={(newValue) => {
              handleFilterChange('date', newValue ? newValue.format('YYYY-MM-DD') : '');
            }}
            sx={{ minWidth: 160, width: '100%' }}
          />
        );
      case 'booking_percentage':
        return (
          <FormSelect
            key={column.field}
            label=""
            value={filters.booking_percentage}
            onChange={(e) => handleFilterChange('booking_percentage', e.target.value)}
            displayEmpty
            sx={{ minWidth: 120 }}
          >
            <MenuItem value="">All</MenuItem>
            <MenuItem value="low">Low (&lt; 40%)</MenuItem>
            <MenuItem value="average">Average (40-70%)</MenuItem>
            <MenuItem value="high">High (70-100%)</MenuItem>
            <MenuItem value="full">Full Booked (100%)</MenuItem>
          </FormSelect>
        );
      default:
        return <div key={column.field} />;
    }
  });

  return (
    <Box className="inventory-list-container">
      {error ? (
        <Box sx={{ mb: 2, p: 2, bgcolor: 'error.light', color: 'error.contrastText', borderRadius: 1 }}>
          {error}
        </Box>
      ) : null}

      <DataTable
        columns={columns}
        data={inventoryData}
        loading={loading}
        pagination={pagination}
        onPageChange={handlePageChange}
        onPerPageChange={handlePerPageChange}
        onSortChange={handleSortChange}
        sortConfig={sortConfig}
        emptyMessage="No inventory data found for today or past dates."
        stickyHeader
        filterRow={filterRowCells}
      />

      <InventoryDateDetailDrawer
        open={drawerOpen}
        date={selectedDateIso}
        themeMode={currentTheme}
        onClose={() => setDrawerOpen(false)}
      />
    </Box>
  );
};

export default InventoryList;
